import React, { useMemo } from "react";
import { TableCell, Box, TableRow, makeStyles } from "@material-ui/core";
import clsx from "clsx";

import DeviceStatus from "./DeviceStatus";
import Sensor from "./Sensor";
import FormattedDate from "../../elements/FormattedDate";
import useFormat from "../../hooks/useFormat";
import { alpha } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  cell: {
    "&:hover": {
      cursor: "pointer"
    }
  },
  row: {
    "&:hover": {
      cursor: "pointer"
    }
  },
  inactive: {
    background: alpha(theme.palette.grey.A200, 0.3),
    opacity: 0.6
  }
}));

const Row = ({ device, onSelect, showColumn, sensorSettings }) => {
  const { formatNumber } = useFormat();
  const classes = useStyles();

  const rowClasses = clsx(classes.row, {
    [classes.inactive]: !device.active
  });

  // Memoize filtered sensors
  const connectedSensors = useMemo(
    () => device.sensors.filter(sensor => sensor.isConnected),
    [device.sensors]
  );

  // Memoize formatted sim credit
  const formattedSimCredit = useMemo(
    () => device.simCreditQty && formatNumber(device.simCreditQty, 2),
    [device.simCreditQty, formatNumber]
  );

  return (
    <TableRow
      className={rowClasses}
      hover
      key={device.id}
      onClick={() => onSelect(device.id)}
    >
      {showColumn("id") && (
        <TableCell className={classes.cell} scope="row">
          {device.id}
        </TableCell>
      )}
      {showColumn("username") && (
        <TableCell className={classes.cell} scope="row">
          <span title={device.user.name}>{device.user.name}</span>
        </TableCell>
      )}
      {showColumn("name") && (
        <TableCell className={classes.cell} scope="row">
          <span title={device.name}>{device.name}</span>
        </TableCell>
      )}
      {showColumn("project") && (
        <TableCell className={classes.cell} scope="row">
          <span title={device.project}>{device.project}</span>
        </TableCell>
      )}
      {showColumn("countryCode") && (
        <TableCell>{device.address.countryCode}</TableCell>
      )}
      {showColumn("postalCode") && (
        <TableCell>{device.address.postalCode}</TableCell>
      )}
      {showColumn("city") && (
        <TableCell>
          <span title={device.address.city}>{device.address.city}</span>
        </TableCell>
      )}
      {showColumn("street") && (
        <TableCell className={classes.cell} scope="row">
          <span
            title={`${device.address.street} ${device.address.streetNumber}`}
          >
            {device.address.street} {device.address.streetNumber}
          </span>
        </TableCell>
      )}
      {showColumn("lastReportedAt") && (
        <TableCell className={classes.cell}>
          <FormattedDate>{device.lastReportedAt}</FormattedDate>
        </TableCell>
      )}
      {showColumn("simCredit") && (
        <TableCell align="right">{formattedSimCredit}</TableCell>
      )}
      {showColumn("status") && (
        <TableCell align="center">
          <DeviceStatus device={device} />
        </TableCell>
      )}

      <TableCell className={classes.cell}>
        <Box display="flex">
          {connectedSensors.map(sensor => (
            <Box width="25%" paddingLeft={1} key={sensor.id}>
              <Sensor
                key={sensor.id}
                sensor={sensor}
                inactiveDevice={!device.active}
                sensorSettings={sensorSettings}
              />
            </Box>
          ))}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default React.memo(Row, (prevProps, nextProps) => {
  return (
    prevProps.device.id === nextProps.device.id &&
    prevProps.device.lastReportedAt === nextProps.device.lastReportedAt &&
    prevProps.device.active === nextProps.device.active &&
    prevProps.sensorSettings === nextProps.sensorSettings &&
    prevProps.showColumn === nextProps.showColumn
  );
});
